import { useUserStore } from '~/stores/user'
import { useOrganizationBySlugQuery } from '~/queries/organization/useOrganizationBySlugQuery'

// Resolves the organisation for the current `[organizationSlug]` route.
//
// Members resolve from the user's own membership list (GET /users/me, via the
// user store). Super users are allowed to open any organisation route (see
// middleware/05.access + system-admin's "Open organisation settings"), including
// orgs they do NOT belong to — those are absent from /users/me, so for a super
// user with a non-membership slug we fall back to resolving the org by slug
// (GET /organizations/by-slug/:slug). The fallback query is gated to that exact
// case, so it never fires for regular users. A slug that resolves to neither
// stays `undefined`, which the page turns into a not-found guard.
export function useCurrentOrganization() {
  const route = useRoute()
  const userStore = useUserStore()

  const organizationSlug = computed(() => String(route.params.organizationSlug))

  const membershipOrganization = computed(() =>
    userStore.organizations.find((org) => org.slug === organizationSlug.value),
  )

  // Super user viewing an org they don't belong to → resolve it by slug.
  const needsFallback = computed(
    () => userStore.isSuperUser && !membershipOrganization.value,
  )
  const { data: fallbackOrganization, isPending: isFallbackPending } =
    useOrganizationBySlugQuery(organizationSlug, needsFallback)

  const organization = computed(
    () =>
      membershipOrganization.value ?? fallbackOrganization.value ?? undefined,
  )

  // True while the async super-user by-slug fallback has yet to settle. A
  // consumer that guards on a missing org (not-found → redirect) must wait for
  // this to clear first, or it would redirect away a valid org mid-fetch.
  // Uses the query's `pending` status (not `isLoading`) so it reads true the
  // instant the fallback enables — `isLoading` also requires `isFetching`, which
  // lags by a microtask and lets an early guard slip through and redirect.
  const isResolving = computed(
    () => needsFallback.value && isFallbackPending.value,
  )

  return { organization, organizationSlug, isResolving }
}
